import * as Sentry from '@sentry/react'

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Only initializes in production environment when DSN is configured
 */
export const initSentry = (): void => {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const environment = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE
  const appVersion = import.meta.env.VITE_APP_VERSION || 'unknown'
  const debug = import.meta.env.VITE_SENTRY_DEBUG === 'true'

  // Only initialize Sentry in production with a valid DSN
  if (!dsn || environment === 'development') {
    console.warn('[Sentry] Skipping initialization (not production or no DSN configured)')
    return
  }

  Sentry.init({
    dsn,
    environment,
    release: appVersion,
    debug,

    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring - capture 10% of transactions in production
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Session Replay - capture 10% of sessions with errors
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Filter out certain errors
    beforeSend(event, hint) {
      // Don't send errors in development
      if (environment === 'development') {
        return null
      }

      // Filter out network errors that are expected
      const error = hint.originalException
      if (error instanceof Error) {
        // Ignore cancelled requests
        if (error.message.includes('canceled') || error.message.includes('aborted')) {
          return null
        }
      }

      return event
    },

    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      // Network errors
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      // Keycloak errors that are handled
      'login_required',
    ],
  })

  // Informational only; use warn because no-console allows warn/error.
  console.warn(`[Sentry] Initialized for ${environment} environment`)
}

/**
 * Capture an exception and send it to Sentry
 */
export const captureException = (
  error: Error,
  context?: Sentry.CaptureContext
): string => {
  return Sentry.captureException(error, context)
}

/**
 * Capture a message and send it to Sentry
 */
export const captureMessage = (
  message: string,
  level?: Sentry.SeverityLevel
): string => {
  return Sentry.captureMessage(message, level)
}

/**
 * Set user context for Sentry
 */
export const setUser = (user: Sentry.User | null): void => {
  Sentry.setUser(user)
}

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb): void => {
  Sentry.addBreadcrumb(breadcrumb)
}

/**
 * Set custom context
 */
export const setContext = (name: string, context: Record<string, unknown>): void => {
  Sentry.setContext(name, context)
}

/**
 * Set a tag for filtering in Sentry
 */
export const setTag = (key: string, value: string): void => {
  Sentry.setTag(key, value)
}
